// ./src/types/filters.ts
// Universal filter types for entity pages — which entity a page manages, and the
// cross-page filter state preserved in the URL as you navigate.

// ============================================
// Entity Types
// ============================================

/** Every entity a page can manage. Keep in step with the sitemap keys. */
export type EntityType =
  // Account-level
  | "tags"
  | "users"
  | "invitations"
  | "memberships"
  | "membership-types"
  | "causes"
  | "contacts"
  | "highlights"
  | "categories"
  | "policies"
  | "tax-settings"
  | "vouchers"
  | "rental-assets"
  | "inventory-items"
  | "artists"
  | "purchase-orders"
  | "marketing-channels"
  | "venues"
  | "vendors"
  | "volunteer-roles"
  | "partner-packages"
  // Event-scoped
  | "pois"
  | "event-volunteers"
  | "accommodations"
  | "signup-items"
  | "poll-questions"
  | "notes"
  | "reminders"
  | "tasks"
  | "budget-items"
  | "rentals"
  | "qrTags"
  | "event-inventory"
  | "timeline-items"
  | "travel-segments"
  | "tallies"
  | "event-map"
  | "event-guests"
  | "event-crew"
  | "event-schedule"
  | "event-tickets"
  | "event-questions"
  | "event-discount-codes"
  | "event-coupons"
  | "event-marketing-channels"
  | "event-guest-groups"
  | "event-guest-draws"
  | "event-games"
  | "event-compliance"
  | "event-website-pages"
  | "event-website-page";

// ============================================
// Universal Entity Filters
// ============================================

/**
 * Cross-page filter state, carried in the URL as the user navigates.
 *
 * NOTE: no page writes these today — event-scoped pages own their own filter
 * state (see the em-scaffold-ui skill: an <XFilterBar> + FilterDrawer per page).
 * The mechanism is kept for filters that should genuinely survive navigation
 * (a date range, a tag selection); the car-specific ones (carId, expenseType)
 * came from the template this app was scaffolded from and are gone.
 */
export interface EntityFilters {
  /** Date range start (ISO date string YYYY-MM-DD) */
  dateFrom: string | null;

  /** Date range end (ISO date string YYYY-MM-DD) */
  dateTo: string | null;

  /** Selected tag IDs */
  tagIds: string[];

  /** Selected year */
  year: number | null;
}

// ============================================
// Filter Schema for Serialization
// ============================================

export type FilterFieldType = "string" | "string[]" | "date" | "number";

export interface FilterFieldSchema {
  /** Type of the filter field for serialization */
  type: FilterFieldType;

  /** URL parameter name */
  urlParam: string;

  /** Default value when not set */
  defaultValue: unknown;
}

export type FilterSchema = Record<keyof EntityFilters, FilterFieldSchema>;

// ============================================
// Default Values
// ============================================

export const DEFAULT_ENTITY_FILTERS: EntityFilters = {
  dateFrom: null,
  dateTo: null,
  tagIds: [],
  year: null,
};

export const ENTITY_FILTER_SCHEMA: FilterSchema = {
  dateFrom: {
    type: "date",
    urlParam: "dateFrom",
    defaultValue: null,
  },
  dateTo: {
    type: "date",
    urlParam: "dateTo",
    defaultValue: null,
  },
  tagIds: {
    type: "string[]",
    urlParam: "tagIds",
    defaultValue: [],
  },
  year: {
    type: "number",
    urlParam: "year",
    defaultValue: null,
  },
};

// ============================================
// Navigation Mode Types
// ============================================

export type EntityNavigationMode = "list" | "add" | "edit" | "view" | "waypoints";